/**
 * Typed notification registry — the single source of truth for what each
 * notification type means: its title/body (built from the `data` payload),
 * icon, group, and whether/how it's actionable.
 *
 * Lives in @papan/types (dependency-free) so all four layers read ONE table
 * instead of hand-keeping four copies of type knowledge:
 *   - the emit sites (features/org-connections/api/routes.ts)
 *   - the list endpoint's actionability stamp (features/files/api/notifications.ts)
 *   - the bell's render + action gate (shared/core-ui/ui/NotificationBell.tsx)
 *   - the click router (apps/web/src/App.tsx)
 *
 * The DB `type` column stays free text; this registry validates + describes in
 * code, and `describeNotification` falls back gracefully for unknown/legacy
 * types so one bad row never crashes the bell.
 *
 * ─── Copy here is ENGLISH-ONLY, and that is a structural limitation ───
 *
 * Every string this registry builds is English, and so is every email
 * template in `shared/auth/src/email-templates.ts`. That is NOT an
 * oversight per type — it cannot currently be otherwise:
 *
 *   - `@papan/i18n` depends on `react-i18next` and declares a `react`
 *     peer dependency. It has ZERO server-side consumers.
 *   - This package is deliberately dependency-free (zod only), because
 *     all four layers above — including the API and worker — import it.
 *     Pulling a React package in here would be a layering break.
 *   - Emails render in the worker/API, which likewise cannot import it.
 *
 * So a notification's language is fixed at the point of *rendering*, and
 * rendering happens server-side with no access to the locale framework —
 * and no recipient locale is threaded to the emit sites either.
 *
 * **Do not "fix" this for one notification type.** Localizing it needs a
 * framework-agnostic `i18next` instance usable from Node, plus a resolved
 * recipient locale carried to every emit site. That is a cross-cutting
 * slice affecting every existing type at once. Adding i18n resource keys
 * for a single type would produce dead keys nothing reads.
 *
 * Context: this was hit while building budget threshold alerts, whose
 * plan required four locales — see the ⚠️ in
 * docs/plans/2026-07-16_BUDGET_GOVERNANCE.md §Phase 4.
 */

import {
  formatMoney,
  formatMoneyLabeled,
  isApproximateCurrency,
  APPROXIMATE_CURRENCY_MARKER,
  type BudgetCap,
} from './budget.js'
import type { PromoEffect } from './promo.js'

/** Notification types rendered in the bell, plus the live-only system signals. */
export type NotificationType =
  | 'share_request_received'
  | 'connection_awaiting_confirm'
  | 'connection_made_by_member'
  | 'reshare_awaiting_source_approval'
  | 'connection_established'
  | 'folder_shared'
  | 'folder_access_granted'
  | 'folder_share_restored'
  | 'shared_reused_no_access'
  | 'shared_contact_needs_access'
  | 'connection_declined'
  /** #1213: the sender's request was closed because the organization it was addressed to was deleted. */
  | 'share_request_closed'
  // #758: a member whose chat-storage limit is full asking an admin to
  // raise it. The only recourse they have — their own page cannot create
  // space that the organisation has not got.
  | 'chat_storage_more_space'
  | 'chat_storage_raised'
  | 'chat_storage_declined'
  // #758 part 2: an admin cleared idle conversations, and some of them were
  // this member's. Sent to the CONVERSATION'S OWNER, which is not always the
  // person whose chat-storage figure the admin was looking at — see the
  // route. The member finds out from this or not at all: the conversation is
  // simply gone the next time they look.
  | 'chat_storage_cleaned'
  // #758 part 2: an admin has BOOKED a cleanup of this member's idle
  // conversations. Sent the moment it is booked, which is the whole reason
  // a booking has a date — the days between this and the deletion are when
  // the member can save or star what matters.
  | 'chat_storage_cleanup_scheduled'
  | 'reshare_send_failed'
  | 'budget_threshold'
  | 'usage_credits_low'
  | 'promo_granted'
  | 'promo_expiring'
  | 'share_link_from_connection'
  // #1229: privileged changes, told to the owners/admins who own that power (also stored in privileged_changes, 0324).
  | 'platform_admin_changed'
  | 'mcp_verified_app_changed'
  | 'mcp_connections_switch_changed'
  | 'mcp_secrets_switch_changed'
  // #982: a cross-organisation move's queued take-back ran out of retries — the mover is told.
  | 'move_incomplete'
  | 'move_disk_undo_failed'
  | 'db_operation_ended'
  // Refused-write undo §5: a run that ended without finishing (or whose processes could not be confirmed stopped) left
  // changes the drive did not accept; the settle pass put them back in order. Counts only.
  | 'refused_writes_undone'
  // Silent — consumed as toasts / refresh signals, never shown in the bell.
  | 'invite_refresh'
  | 'share_repointed'
  | 'share_repoint_failed'

/** The `data` payload carried on notifications. Free-form (jsonb-backed), but
 *  the fields the emit sites actually write are named for type-safe builders. */
export interface NotificationData {
  /** #1229 privileged changes: who was changed / what, by whom, and the new state. */
  changeSubject?: string
  changeActor?: string
  changeAfter?: string
  requestId?: string
  /** move_incomplete (#982): the moved item's name. */
  itemName?: string
  /** refused_writes_undone: how many changes were undone, left (a newer version, or not undoable), and still waiting. */
  undoneCount?: number
  leftCount?: number
  waitingCount?: number
  /** refused_writes_undone: files too large to check — left as they are */
  largeUncheckedCount?: number
  /** refused_writes_undone: items that couldn't be read (a folder, a link, a hard link) — left as they are */
  unreadableCount?: number
  fromEmail?: string
  fromName?: string
  orgName?: string
  /** share_request_received: set when the request was reopened via decline-and-resend. */
  reopened?: boolean
  /** share_request_received (with `reopened`): a block met at the sender's confirm or the
   *  owner's approval sent it back — the organization picked can't take it (#1145). */
  returned?: boolean
  resendNote?: string
  recipientEmail?: string
  recipientName?: string
  /** shared_contact_needs_access: the person a re-share NAMED who cannot open the
   *  recipient org's reused `.shared` (#773) — who the managers are asked about. */
  contactEmail?: string
  contactName?: string
  pickedTenantName?: string | null
  initiatorOrgName?: string
  /** connection_established/declined: the initiator's email (set only when the
   *  initiator is a person) — the stable avatar seed for that party. */
  initiatorEmail?: string
  /** connection_declined: the org/person that declined (the recipient when they
   *  reject an incoming request; the initiator when they reject after the pick).
   *  Named in the body so a stack of declines says WHO declined which pair. */
  declinedByOrgName?: string
  /** share_request_closed (#1213): the deleted organization the closed request was addressed to. */
  recipientOrgName?: string
  /** share_request_closed (#1213): `'person'` when the deleted recipient was a person's account (their own drive), not an organization. */
  recipientKind?: 'org' | 'person'
  /** connection_declined: the decliner's email (when a person) — the stable
   *  avatar seed so the decliner matches their avatar elsewhere. */
  declinedByEmail?: string
  /** connection_declined: true only on the INITIATOR's copy (they can re-send the
   *  request). Gates the "Send a new request" affordance off the rejected
   *  recipient's copy. */
  canReconnect?: boolean
  /** connection_established: the folder/file shared along with the connection
   *  (absent for a connection-only initiate). Shown in the body. */
  sharedName?: string
  /** connection_established / folder_shared: when a multi-folder share bundled
   *  EXTRA folders beyond the primary (`sharedName`), the resolved NAMES of those
   *  extras. Stamped at emit so the registry can list them live; absent for a
   *  single-folder share (keeps that copy byte-identical). */
  additionalFolderNames?: string[]
  /** connection_established: the folder to open from the "View folder" link —
   *  the initiator's source folder, or the recipient's `.shared` (each side gets
   *  the id navigable in ITS OWN drive). */
  sharedFolderId?: string
  /** connection_established / folder_shared / folder_share_restored: the tenant in
   *  whose drive `sharedFolderId` is navigable (this side's recipient/source tenant).
   *  Lets the bell deep-link cross-tenant — the shared folder may live in a different
   *  tenant than the one the user is currently viewing. */
  sharedFolderTenantId?: string
  /** Whether the shared TARGET is a folder. A FILE share renders "View file"
   *  and REVEALS the row in its parent — opening a file-target `.shared` as a
   *  folder shows an empty listing. Absent (legacy rows) ⇒ folder behavior.
   *  NOTE: stamped from the TARGET kind — a recipient's `.shared` pointer row
   *  itself is always is_folder=false, so the emitter must look through it. */
  sharedIsFolder?: boolean
  /** The parent folder of `sharedFolderId` at emit time — the reveal target
   *  for a FILE share (open the drive folder CONTAINING it, selected). The
   *  mount is movable so this can go stale; the drive then just opens the
   *  parent without a selection — a soft miss, not an error. */
  sharedParentFolderId?: string
  /** folder_access_granted: the team the grant was made to, when the recipient
   *  reached the folder through a team rather than by name. Absent ⇒ the grant
   *  named them individually. Carries the team NAME, not its id — the body says
   *  which team, and a recipient who is in it already knows it exists. */
  grantedToTeamName?: string
  /** connection_made_by_member: the member who formed the connection and the
   *  peer org. `orgName` carries THIS side's org name. */
  connectorEmail?: string
  connectorName?: string
  otherOrgName?: string
  /** The identity this notification was addressed to (shown as a subline). */
  notifiedEmail?: string
  referrerName?: string
  referrerEmail?: string
  /** share_request_received (reshare): who the recipient's Decline & block blocks — the
   *  request's decline target (the resharer while the owner hasn't decided, else the owner). */
  declineTargetEmail?: string
  /** share_request_received / connection_established: the request is a RESHARE
   *  referral (referrer B reshared owner A's content). Copy then names the
   *  referrer + folder and frames it as a shared folder, not a bare
   *  connection request — a first-time recipient may never have heard of the
   *  owner, only of the person who shared with them. */
  viaReshare?: boolean
  /** share_request_received (reshare): the recipient tenant the reshare targets
   *  (the org they hold the resharer connection through). The accept popup
   *  pre-selects it in the "Receive as" picker. Absent ⇒ personal-targeted. */
  targetTenantId?: string
  targetTenantName?: string
  /** The request was SHARE-driven (a folder rode along) rather than a bare
   *  connection request from the Network page. The connection still forms
   *  behind the scenes, but every message frames the FOLDER — recipients who
   *  were just shared a folder shouldn't be reading connection-protocol
   *  language. Connection-only initiates (Network page) keep the old copy. */
  shareDriven?: boolean
  canonicalSharedId?: string | null
  /** connection_established sub-situation (which of the four "now live"
   *  framings to use), so one type covers all four without hand-forked titles. */
  kind?: 'accepted' | 'connected' | 'live' | 'already'
  /** Distinguishes the org-connections handshake from the share-links
   *  recipient-initiated mount, so the title can use mount-specific wording
   *  ("Add-to-Drive request" / "Shared folder added to your Drive"). */
  source?: 'org_connection' | 'share_link_mount'
  /** budget_threshold: which ceiling was crossed. `user` is addressed to the
   *  member personally; both `tenant_*` scopes are addressed to admins about
   *  the ORG; `storage` is bytes, not money. */
  budgetScope?: 'user' | 'tenant_free' | 'tenant_extra' | 'storage'
  /** budget_threshold: WHICH tenant overspent, so the action opens that org's
   *  budget rather than whichever one the reader happens to be looking at.
   *
   *  A person's notifications span every tenant they belong to, and an admin of
   *  two orgs reading an alert about one while working in the other would
   *  otherwise land on the wrong org's numbers — and could change the wrong
   *  caps. Optional because rows emitted before this field existed do not have
   *  it; the handler falls back to the active tenant, which is what it always
   *  did. */
  budgetTenantId?: string
  /** budget_threshold: `pct` = a fraction of a KNOWN cap was crossed (only ever
   *  evaluated on capped axes); `abs` = a raw spend figure was reached, which
   *  is also evaluated on UNLIMITED axes — so an `abs` alert must never render a
   *  percentage nor imply that a limit exists. */
  thresholdKind?: 'pct' | 'abs'
  /** The threshold that fired: a percentage (`pct`) or microdollars (`abs`). */
  thresholdValue?: number
  /** Money scopes: microdollars spent in `period`. */
  usedBillingMicro?: number
  /** Money scopes: the cap in microdollars. `null` = unlimited (tracked, no
   *  ceiling) and `0` = hard block — two different states, never collapsed. */
  capBillingMicro?: BudgetCap
  /** Storage scope: bytes. Same null/0 distinction as the money pair. */
  usedBytes?: number
  capBytes?: number | null
  /** The usage window as `YYYY-MM`. */
  period?: string
  /** Display currency stamped at emit, so the copy reads the same in the bell
   *  as in the dashboard even if an admin later changes the setting. */
  displayCurrency?: string
  displayCurrencyRate?: number
  /** usage_credits_low: the prepaid balance in microdollars when the warning
   *  fired, and the threshold it crossed. */
  creditBalanceBillingMicro?: number
  creditThresholdBillingMicro?: number
  /** usage_credits_low: whether the threshold was set by an admin or derived
   *  from the last top-up. The copy says "your low-balance alert" for an
   *  explicit one and stays neutral for the default, so a tenant that never
   *  configured anything isn't told it crossed a limit it chose. */
  creditThresholdSource?: 'explicit' | 'default_last_topup'
  /** promo_granted / promo_expiring: the grant this is about, and what it
   *  carries. `promoEffects` is the stored `promo_grants.effects` verbatim —
   *  the copy summarises it, so a future effect kind degrades to "promotional
   *  access" rather than throwing (same defense-in-depth stance the entitlement
   *  resolver takes on unknown kinds). */
  promoGrantId?: string
  promoEffects?: PromoEffect[]
  /** ISO timestamp of `promo_grants.ends_at`. Rendered as an ABSOLUTE date so a
   *  row re-described weeks later still states something true — the bell
   *  re-runs `describeNotification` on every render, it does not replay the
   *  stored title. */
  promoEndsAt?: string
  /** promo_expiring: whole days between the emit and `ends_at`, STAMPED at emit
   *  and never recomputed. Frozen like every other figure in this registry;
   *  the absolute `promoEndsAt` in the body is what corrects a stale row. */
  promoDaysLeft?: number
  /** promo_expiring: unspent credit on the grant at emit (microdollars). Absent
   *  for a grant carrying no `credit` effect. */
  promoCreditRemainingBillingMicro?: number
  /** promo_granted: whether an admin issued the grant directly or someone on
   *  the tenant redeemed a code. The copy differs — "you redeemed" is a claim
   *  about the reader's own action and must not be made about an admin grant. */
  promoSource?: 'admin' | 'code'
  /** share_link_from_connection: the link a connected app (an MCP client) created as the person — the review dialog
   *  reads (and may revoke) it, in the tenant that owns it. */
  shareLinkId?: string
  shareLinkTenantId?: string
  /** The identity (membership email) the person acted as in that tenant — the Revoke request sends it with the
   *  tenant, so it works whichever org is active when the bell is clicked. */
  shareLinkOrgEmail?: string
  /** share_link_from_connection: what the link opens, who it is for, and which connection made it. */
  linkTargetName?: string
  linkAudience?: 'public' | 'restricted'
  linkRecipients?: string[]
  connectionName?: string
  /** db_operation_ended (#1101): the background db operation, how it ended and why (`DbOperationEnded`), and the
   *  db's NAME as the person's request reached it — never a path. `fileId` is the request's own id (a route through a
   *  `.shared` stays a route). */
  dbOperation?: string
  dbOutcome?: string
  dbReason?: string
  dbFileName?: string
  fileId?: string
  [key: string]: unknown
}

/** What clicking the notification does. `route` is a stable key the app layer
 *  maps to a popup/navigation; `requestState` (when present) is the
 *  share_requests.state in which the action is still live — the server stamps
 *  `actionable` from it, the bell gates on it, the click re-validates against
 *  it (all three read THIS field instead of three hand-kept copies). */
export interface NotificationAction {
  route: 'accept' | 'confirm' | 'network' | 'reshare-approve' | 'reshare-retry' | 'reconnect' | 'view-folder'
    /** Settings → Organization → Budget. The org's own spend, not the platform
     *  console. `network` was already a settings destination, so this is a
     *  second one rather than a new kind of route. */
    | 'org-budget'
    /** Settings → Billing — where credit is bought. Distinct from `org-budget`
     *  because they answer different questions and sit behind different
     *  capabilities: `manage_budget` reads spend, `manage_billing` moves money.
     *  Used by `usage_credits_low`, whose copy points here in as many words. */
    | 'org-billing'
    /** Settings → Storage. A third settings destination, for the same
     *  reason the two above are separate: it answers a different question
     *  and the admin arriving there is doing a different job (#758). */
    | 'org-storage'
    /** Decline a member's request for more chat storage, from the bell. */
    | 'chat-storage-decline'
    /** Review the share link named in the data (`shareLinkId`, `shareLinkTenantId`) — who made it, when, who can open
     *  it — and keep or revoke it there. `share_link_from_connection`. */
    | 'review-share-link'
  label?: string
  /** Request-bound actions: live only while the request is in this state.
   *  Omitted for always-available next-step links (the dead-end fixes). */
  requestState?:
    | 'pending_recipient'
    | 'pending_initiator_confirm'
    | 'pending_source_approval'
  /**
   * ── There is deliberately NO permission field here ──────────────────
   *
   * `requiresOwnerAdmin?: boolean` used to sit at this spot. It was declared,
   * set on exactly one entry, and read NOWHERE — no bell gate, no server stamp,
   * no click re-validation — four lines below a docblock describing
   * `requestState` being read in all three places. A field that names a
   * restriction and enforces none is worse than no field: it reads as a
   * guarantee to anyone adding the next action.
   *
   * It was also redundant. Both actions that would have wanted it —
   * `connection_made_by_member` and `budget_threshold`'s tenant scopes — are
   * emitted ONLY to owners and admins, so the recipient set is already the
   * permitted set. Restricting delivery is strictly stronger than hiding a
   * button from someone who received the row anyway.
   *
   * If a future action needs a genuine render-time gate, note what blocks the
   * obvious implementation: the bell has no per-tenant capability data, because
   * a person's notifications span every tenant they belong to. Gate at EMIT
   * unless that changes.
   */
}

export type NotificationIcon = 'share' | 'link' | 'shield' | 'check' | 'alert' | 'bell'

/** How a request-bound notification renders once its request has LEFT
 *  `action.requestState` — i.e. the thing it asked for has been settled.
 *
 *  Without this a settled row just loses its affordance and sits there stating a
 *  question that already has an answer, which is why the outcome had to be
 *  announced by a SECOND notification. Resolving it here lets one row carry the
 *  whole story: ask → outcome. `actionable:false` alone can't do that job — it
 *  can't tell approved from declined from expired. */
export interface ResolvedOutcome {
  badge: { text: string; tone: 'success' | 'danger' | 'muted' }
  /** The follow-on step, if the outcome has one (e.g. approved ⇒ open the
   *  folder). Omitted ⇒ terminal, nothing left to do, row not clickable. */
  action?: NotificationAction
}

export interface NotificationDescriptor {
  /** Icon KEY — the bell maps it to a lucide component, keeping this module
   *  free of any UI dependency. */
  icon: NotificationIcon
  group: 'connection' | 'reshare' | 'system'
  /** Live-only system signals: never persisted to the bell list. */
  silent?: boolean
  buildTitle: (d: NotificationData) => string
  buildBody?: (d: NotificationData) => string | undefined
  action?: NotificationAction
  /** Data-aware variant of `action` — wins over the static one in
   *  `describeNotification`. Keep the static `action` alongside it as the
   *  stable fallback: `ACTIONABLE_STATE_BY_TYPE` reads ONLY the static
   *  field, so request-gating never depends on payload data. */
  buildAction?: (d: NotificationData) => NotificationAction | undefined
  /** Map the request's CURRENT state to a settled badge (+ optional next step).
   *  Only consulted once the row is no longer actionable. Return undefined for
   *  states with nothing to say — the row then renders exactly as it does today. */
  resolveOutcome?: (state: string, d: NotificationData) => ResolvedOutcome | undefined
  /** See `DescribedNotification.secondaryAction`. */
  secondaryAction?: NotificationAction
}

const whoFrom = (d: NotificationData): string => d.fromName ?? d.fromEmail ?? 'Someone'
const recipOf = (d: NotificationData): string => d.recipientName ?? d.recipientEmail ?? 'someone'
const asStr = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)

/** The two parties to a connection as "OrgA ↔ OrgB" — the identifier that lets
 *  a user tell stacked connection notifications apart. Undefined until both org
 *  names are known (older notifications, or before the recipient picks an org). */
const orgPairLine = (d: NotificationData): string | undefined => {
  const a = asStr(d.initiatorOrgName)
  const b = asStr(d.pickedTenantName)
  return a && b ? `${a} ↔ ${b}` : undefined
}

/** Render the shared-folder phrase for a (possibly multi-folder) share. One
 *  folder → the single quoted name (byte-identical to the old single-folder
 *  copy). Several → an inline quoted list capped at SHARED_FOLDERS_MAX_INLINE
 *  names, then a +N more tail; `plural` drives the noun/verb. Undefined when no
 *  primary name is known, so the caller omits the whole folder clause — extras
 *  are never promoted to lead. */
/** Noun for the shared primary — a single-FILE share says "file" (opening it
 *  as a folder shows an empty listing; the copy must not promise one). Legacy
 *  rows without the stamp keep "folder". */
const sharedNoun = (d: NotificationData): string => (d.sharedIsFolder === false ? 'file' : 'folder')

/** The view CTA for a share — file targets get "View file" (the client then
 *  REVEALS the row in its parent instead of navigating into it); folders and
 *  legacy rows keep "View folder". */
const viewSharedAction = (d: NotificationData): NotificationAction =>
  ({ route: 'view-folder', label: d.sharedIsFolder === false ? 'View file' : 'View folder' })

const SHARED_FOLDERS_MAX_INLINE = 3
const sharedFoldersPhrase = (d: NotificationData): { text: string; plural: boolean } | undefined => {
  const primary = asStr(d.sharedName)
  if (!primary) return undefined
  const extras = Array.isArray(d.additionalFolderNames)
    ? d.additionalFolderNames.filter((n): n is string => typeof n === 'string' && !!n)
    : []
  const all = [primary, ...extras] // primary first, then request order
  if (all.length === 1) return { text: `“${all[0]}”`, plural: false }
  const shown = all.slice(0, SHARED_FOLDERS_MAX_INLINE)
  const rest = all.length - shown.length
  const quoted = shown.map((n) => `“${n}”`).join(', ')
  return { text: rest > 0 ? `${quoted} +${rest} more` : quoted, plural: true }
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** `'2026-07'` → `'July'`. Parsed by hand rather than through `Date`, whose
 *  `'YYYY-MM'` handling is UTC-anchored and shifts the month for anyone west of
 *  Greenwich. Undefined for a missing/malformed period, so the caller drops the
 *  whole clause instead of printing a wrong month. */
const monthName = (period: unknown): string | undefined => {
  const m = /^(\d{4})-(\d{2})$/.exec(typeof period === 'string' ? period : '')
  return m ? MONTH_NAMES[Number(m[2]) - 1] : undefined
}

const budgetMoney = (d: NotificationData) => ({
  currency: asStr(d.displayCurrency) ?? 'USD',
  rate: typeof d.displayCurrencyRate === 'number' ? d.displayCurrencyRate : 1,
})

/** Bytes as a short human figure. Storage thresholds are about capacity, so
 *  they never go through `formatMoney`. */
const formatBytes = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let v = Math.abs(bytes)
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${(bytes < 0 ? -v : v).toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/** `'2026-08-15T…Z'` → `'15 August 2026'`, in UTC.
 *
 *  Deliberately not `toLocaleDateString`: this same string is built in the
 *  worker (email), in the API (the stored row) and in the browser (the bell
 *  re-describes on every render), and a locale/timezone-dependent formatter
 *  would print three different dates for one `ends_at`. Undefined for a
 *  missing/unparseable value so the caller drops the clause. */
const promoDate = (iso: unknown): string | undefined => {
  if (typeof iso !== 'string' || !iso) return undefined
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return undefined
  const d = new Date(t)
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** One promo effect as a customer-facing English phrase.
 *
 *  Returns undefined for an unrecognised kind rather than printing it raw —
 *  the same defense-in-depth the entitlement resolver applies (an effect kind
 *  retired in a future version degrades to a no-op, so it must degrade to
 *  silence here too, not to `[object Object]` in someone's inbox). */
const promoEffectPhrase = (e: PromoEffect, money: { currency: string; rate: number }): string | undefined => {
  // `data` is free-form jsonb and this registry's contract is that it NEVER
  // throws — a single malformed row must not crash the bell. `[null]` is the
  // shape that would, so it is checked before the discriminant is read.
  if (!e || typeof e !== 'object') return undefined
  switch (e.kind) {
    case 'plan_override': return `access to the ${e.planId} plan`
    case 'credit': return `${formatMoney(e.amountBillingMicro, money)} in AI credit`
    case 'storage_boost': return `${formatBytes(e.bytes)} of extra storage`
    case 'concurrency_boost': return `${e.slots} extra agent ${e.slots === 1 ? 'slot' : 'slots'}`
    case 'feature_unlock': return e.features.join(', ')
    default: return undefined
  }
}

/** Every effect on a grant as one phrase — "A, B and C" — plus whether any of
 *  them printed a CONVERTED money figure.
 *
 *  `hasMoney` is not decoration: storage is bytes and concurrency is a count,
 *  neither of which is ever converted, so a grant of "4 GB of extra storage" on
 *  an MYR tenant must not be marked `≈`. Only a `credit` effect puts a
 *  rate-converted number in the sentence. `text` is undefined when the grant
 *  carries nothing this version understands, so callers fall back to the
 *  generic "promotional access" wording instead of an empty clause. */
const promoEffectsPhrase = (
  d: NotificationData,
  money: { currency: string; rate: number },
): { text?: string; hasMoney: boolean } => {
  const effects = Array.isArray(d.promoEffects) ? d.promoEffects : []
  const parts = effects
    .map((e) => promoEffectPhrase(e, money))
    .filter((p): p is string => !!p)
  const hasMoney = effects.some((e) => e?.kind === 'credit')
  const text = parts.length === 0 ? undefined
    : parts.length === 1 ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
  return { text, hasMoney }
}

/** Mark a whole LINE as converted, once — never each figure inside it. Same
 *  rule budget_threshold's body follows: "≈$40 of ≈$50" reads as two separate
 *  approximations rather than one converted statement. `hasMoney` gates it so a
 *  line carrying only bytes/counts is never marked. */
const markApproximate = (line: string, money: { currency: string; rate: number }, hasMoney: boolean): string =>
  hasMoney && isApproximateCurrency(money.currency, money.rate)
    ? `${APPROXIMATE_CURRENCY_MARKER}${line}`
    : line

/** The used/cap line shared by both threshold kinds. Renders the three cap
 *  states distinctly: a real ceiling, `null` (unlimited — tracked but no
 *  ceiling, so it must NOT read as a limit) and `0` (a hard block, which must
 *  NOT read as unlimited). */
const budgetUsageLine = (
  used: number,
  cap: number | null | undefined,
  fmt: (n: number) => string,
  noun: string,
  when = '',
): string => {
  if (cap === null) return `${fmt(used)} used so far${when} — no ${noun} limit is set.`
  if (cap === 0) return `${fmt(used)} used${when}. This ${noun} is set to ${fmt(0)}, so it is blocked.`
  if (cap === undefined) return `${fmt(used)} used${when}.`
  return `${fmt(used)} of ${fmt(cap)} used${when}.`
}

/** The actor whose avatar fronts the notification — WHO did the thing (the
 *  requester, the accepter, the connector, the decliner, …). Returns the
 *  name/email so the bell can render the shared initials-avatar; undefined for
 *  a system notification with no human/org actor. */
export function notificationActor(type: string, d: NotificationData = {}): { name?: string; email?: string } | undefined {
  switch (type) {
    // A reshare-referral request fronts the REFERRER (the person the recipient
    // actually dealt with), not the owner the connection is with.
    case 'share_request_received': return d.viaReshare
      ? { name: asStr(d.referrerName) ?? asStr(d.fromName), email: asStr(d.referrerEmail) ?? asStr(d.fromEmail) }
      : { name: asStr(d.fromName), email: asStr(d.fromEmail) }
    case 'connection_awaiting_confirm': return { name: asStr(d.recipientName), email: asStr(d.recipientEmail) }
    case 'connection_made_by_member': return { name: asStr(d.connectorName), email: asStr(d.connectorEmail) }
    case 'reshare_awaiting_source_approval': return { name: asStr(d.referrerName), email: asStr(d.referrerEmail) }
    case 'reshare_send_failed': return { email: asStr(d.recipientEmail) }
    // The OTHER party: the recipient who accepted (initiator's "accepted" copy),
    // or the initiator who shared (recipient's "connected" copy).
    case 'connection_established': return d.kind === 'connected'
      ? { name: asStr(d.initiatorOrgName), email: asStr(d.initiatorEmail) }
      : { name: asStr(d.pickedTenantName), email: asStr(d.recipientEmail) }
    case 'folder_shared': return { name: asStr(d.initiatorOrgName), email: asStr(d.initiatorEmail) }
    case 'folder_share_restored': return { name: asStr(d.initiatorOrgName), email: asStr(d.initiatorEmail) }
    case 'shared_reused_no_access': return { name: asStr(d.initiatorOrgName), email: asStr(d.initiatorEmail) }
    case 'shared_contact_needs_access': return { name: asStr(d.contactName), email: asStr(d.contactEmail) }
    case 'connection_declined': return { name: asStr(d.declinedByOrgName), email: asStr(d.declinedByEmail) }
    case 'share_request_closed': return { name: asStr(d.recipientOrgName), email: asStr(d.recipientEmail) }
    default: return undefined
  }
}

export const NOTIFICATION_REGISTRY: Record<NotificationType, NotificationDescriptor> = {
  share_request_received: {
    icon: 'link',
    group: 'connection',
    buildTitle: (d) => d.returned
      ? (d.shareDriven ? `Shared ${sharedNoun(d)} is back — choose where to put it` : 'Connection request is back — choose where to put it')
      : d.reopened
      ? (d.shareDriven ? `Shared ${sharedNoun(d)} returned — pick again` : 'Connection request reopened — pick again')
      : (d.viaReshare || d.shareDriven) ? (d.sharedIsFolder === false ? 'File shared with you' : 'Folder shared with you')
      : 'New connection request',
    buildBody: (d) => {
      if (d.returned) {
        return `${whoFrom(d)}'s request couldn't go to the organization you chose — it has blocked who sent it. Accept it somewhere else, or ask an admin there to lift the block.`
      }
      if (d.reopened) {
        return asStr(d.resendNote) ? `They asked you to pick again: “${d.resendNote}”` : 'They asked you to pick again.'
      }
      // Reshare referral: the recipient dealt with the REFERRER, not the owner
      // this request connects them to — bare "<owner> wants to connect." reads
      // like a cold call from a stranger. Name the referrer and the folder.
      if (d.viaReshare) {
        const referrer = d.referrerName ?? d.referrerEmail ?? 'Someone'
        const owner = whoFrom(d)
        const sf = sharedFoldersPhrase(d)
        return sf
          ? `${referrer} shared ${owner}'s ${sf.text} with you — accept to receive it.`
          : `${referrer} shared a ${sharedNoun(d)} from ${owner} with you — accept to receive it.`
      }
      // Share-driven first contact: the connection forms behind the scenes —
      // the recipient was shared a FOLDER, so say that, not protocol language.
      if (d.shareDriven) {
        const from = `${whoFrom(d)}${d.orgName ? ` (${d.orgName})` : ''}`
        const sf = sharedFoldersPhrase(d)
        return sf
          ? `${from} shared ${sf.text} with you — accept to receive it.`
          : `${from} shared a ${sharedNoun(d)} with you — accept to receive it.`
      }
      return `${whoFrom(d)} wants to connect${d.orgName ? ` with ${d.orgName}` : ''}.`
    },
    action: { route: 'accept', requestState: 'pending_recipient', label: 'Review' },
  },
  connection_awaiting_confirm: {
    icon: 'link',
    group: 'connection',
    buildTitle: (d) => d.source === 'share_link_mount' ? 'Add-to-Drive request'
      : d.shareDriven ? 'Confirm your share'
      : 'Confirm your connection',
    // For a share-link mount the recipient REQUESTED to add the folder — they
    // didn't "accept" anything, and `pickedTenantName` is the Drive they want
    // it in, not a role they're acting as. Word it like the confirm popup
    // ("wants to add … to their <tenant> Drive") so it doesn't read as
    // "<recipient> accepted as <org>".
    buildBody: (d) => {
      if (d.source === 'share_link_mount') {
        return `${recipOf(d)} wants to add your shared folder to their${d.pickedTenantName ? ` ${d.pickedTenantName}` : ''} Drive — confirm to go live.`
      }
      // Share-driven: the sender shared a folder; name it instead of the
      // connection protocol.
      if (d.shareDriven) {
        const sf = sharedFoldersPhrase(d)
        return `${recipOf(d)} accepted ${sf ? sf.text : 'your shared folder'}${d.pickedTenantName ? ` as ${d.pickedTenantName}` : ''} — confirm to go live.`
      }
      return `${recipOf(d)} accepted${d.pickedTenantName ? ` as ${d.pickedTenantName}` : ''} — confirm to go live.`
    },
    action: { route: 'confirm', requestState: 'pending_initiator_confirm', label: 'Confirm' },
  },
  connection_made_by_member: {
    icon: 'link',
    group: 'connection',
    buildTitle: () => 'New connection',
    buildBody: (d) =>
      `${d.connectorName ?? d.connectorEmail ?? 'A member'} connected ${d.orgName ?? 'your organization'} to ${d.otherOrgName ?? 'another organization'}.`,
    // Informational: links to the Network tab (read-only) so an owner/admin can
    // see the connection. No `requestState` ⇒ never stamped actionable.
    //
    // This carried `requiresOwnerAdmin: true` — the only place that field was
    // ever set, and nothing read it. It was also unnecessary: the emitter
    // (`features/org-connections/api/routes.ts`) sends this ONLY to owners and
    // admins, so the recipient set is already the permitted set. Same guarantee
    // `budget_threshold` relies on, and stronger than a hidden button.
    action: { route: 'network', label: 'View' },
  },
  reshare_awaiting_source_approval: {
    icon: 'shield',
    group: 'reshare',
    buildTitle: () => 'A reshare of your content awaits approval',
    // The recipient already picked their identity (Option 2), so name the concrete
    // destination (org / "Personal") the owner is approving when it's known.
    buildBody: (d) => {
      // Name the person, not just their address — the owner is deciding about a
      // human. Same "Name <email>" shape the approval popup's `Actor` renders.
      const email = d.recipientEmail
      const name = asStr(d.recipientName)
      const who = email ? (name ? `${name} <${email}>` : email) : 'someone'
      const dest = asStr(d.pickedTenantName)
      // Name the FOLDER — the owner is approving a specific thing, and "your
      // content" / "this" made them open the popup just to find out which. Same
      // phrase builder (+ curly quotes, multi-folder capping) as every other
      // shared-folder copy. Legacy rows stamped no name → the old wording.
      const sf = sharedFoldersPhrase(d)
      return `${d.referrerName ?? 'Another organization'} wants to share ${sf ? sf.text : 'your content'} with ${who}${dest ? ` (receiving as ${dest})` : ''}.`
    },
    action: { route: 'reshare-approve', requestState: 'pending_source_approval', label: 'Review' },
    // Once approved/declined/expired this row REPLACES the separate outcome
    // notification the owner used to get, so it must name the outcome exactly:
    // `source_approved` lands on `established` (+materialize ⇒ the folder is live,
    // hence a View folder step); the other two are dead ends.
    resolveOutcome: (state, d) => {
      switch (state) {
        case 'established':
          return {
            badge: { text: 'Approved', tone: 'success' },
            action: viewSharedAction(d),
          }
        case 'closed_rejected': return { badge: { text: 'Declined', tone: 'danger' } }
        case 'closed_expired': return { badge: { text: 'Expired', tone: 'muted' } }
        default: return undefined
      }
    },
  },
  connection_established: {
    icon: 'check',
    group: 'connection',
    buildTitle: (d) => {
      const mount = d.source === 'share_link_mount'
      switch (d.kind) {
        // A reshare referral's "accepted" goes to the OWNER, who approved a
        // referral rather than sending a request — "Your connection request
        // was accepted" claims an act they never did. A share-driven request's
        // "accepted" frames the FOLDER (the connection is plumbing).
        case 'accepted': return mount ? 'Add-to-Drive request approved'
          : d.viaReshare ? `Reshared ${sharedNoun(d)} accepted`
          : d.shareDriven ? `Your shared ${sharedNoun(d)} was accepted`
          : 'Your connection request was accepted'
        case 'live': return 'A connection in your network is now live'
        case 'already': return 'Already connected'
        case 'connected':
        default: return (mount || d.shareDriven) ? `Shared ${sharedNoun(d)} added to your Drive` : 'You are now connected'
      }
    },
    buildBody: (d) => {
      if (d.kind === 'already') {
        const sf = sharedFoldersPhrase(d)
        return d.shareDriven && sf
          ? `${d.pickedTenantName ?? 'They'} were already in your network — ${sf.text} is now shared.`
          : `${d.pickedTenantName ?? 'They'} were already in your network — your request was completed automatically.`
      }
      if (d.kind === 'live') return undefined
      // Owner's copy for an accepted RESHARE referral: credit the referrer who
      // actually initiated it.
      if (d.kind === 'accepted' && d.viaReshare) {
        const recip = d.pickedTenantName ?? d.recipientEmail ?? 'The recipient'
        const referrer = d.referrerName ?? d.referrerEmail ?? 'your partner'
        const sf = sharedFoldersPhrase(d)
        return `${recip} accepted the folder${sf ? ` ${sf.text}` : ''} ${referrer} reshared — it's now shared with them.`
      }
      // Share-driven: folder-first copy on both sides; no connection-protocol
      // language (the connection formed behind the scenes).
      if (d.shareDriven) {
        const sf = sharedFoldersPhrase(d)
        if (d.kind === 'accepted') {
          const recip = d.pickedTenantName ?? d.recipientEmail ?? 'They'
          return `${recip} accepted ${sf ? sf.text : `your ${sharedNoun(d)}`} — it's now shared with them.`
        }
        const from = d.initiatorOrgName ?? 'They'
        return sf ? `${from} shared ${sf.text} with you.` : `${from} shared a ${sharedNoun(d)} with you.`
      }
      // Lead with "OrgA ↔ OrgB connection established" so stacked notifications
      // are distinguishable; append the folder(s) shared along (if any). A
      // multi-folder share lists every folder (capped), not just the primary.
      const sf = sharedFoldersPhrase(d)
      const pair = orgPairLine(d)
      if (pair) {
        return `${pair} connection established${sf ? ` · ${sf.text} ${sf.plural ? 'folders' : sharedNoun(d)} shared` : ''}.`
      }
      // Fallback for notifications whose stored data predates the org names.
      if (d.kind === 'accepted') {
        const who = d.pickedTenantName ?? 'They'
        return sf ? `${who} accepted — ${sf.text} ${sf.plural ? 'are' : 'is'} now shared.` : `${who} accepted your connection request.`
      }
      const from = d.initiatorOrgName ?? 'They'
      return sf ? `${from} shared ${sf.text} with you.` : `You're now connected to ${from}.`
    },
    // "View folder"/"View file" opens the shared target (sharedFolderId — each
    // side's own-drive id; file targets REVEAL instead of navigating into the
    // pointer). No requestState ⇒ informational navigation, not approve/reject.
    action: { route: 'view-folder', label: 'View folder' },
    buildAction: viewSharedAction,
  },
  // A folder shared to an ALREADY-connected party (no handshake) — the recipient
  // gets a bell entry, not just the silent realtime file:created.
  folder_shared: {
    icon: 'share',
    group: 'connection',
    buildTitle: (d) => `New ${sharedNoun(d)} shared`,
    buildBody: (d) => {
      const from = asStr(d.initiatorOrgName) ?? 'Someone'
      const sf = sharedFoldersPhrase(d)
      return sf
        ? `${from} shared ${sf.text} with you.`
        : `${from} shared a ${sharedNoun(d)} with you.`
    },
    action: { route: 'view-folder', label: 'View folder' },
    buildAction: viewSharedAction,
  },
  // Intra-org sibling of folder_shared: someone added this person — by name, or
  // through a team they are in — to a folder's access list INSIDE their own
  // organisation. Distinct type because the copy differs on both axes that
  // matter: the sharer is a COLLEAGUE (a person, not a counterparty org), and
  // there is no connection to accept, so the row is informational.
  //
  // The body deliberately does NOT name the permission tier. What was granted
  // is visible on the folder itself and changes without a notification; a body
  // saying "gave you Manage & Edit" would be a claim that silently rots, and
  // the useful half — that there is something new to look at — does not need it.
  folder_access_granted: {
    icon: 'share',
    group: 'connection',
    buildTitle: (d) => `New ${sharedNoun(d)} shared`,
    buildBody: (d) => {
      const from = asStr(d.fromName) ?? asStr(d.fromEmail) ?? 'Someone'
      const name = asStr(d.sharedName)
      const what = name ? `“${name}”` : `a ${sharedNoun(d)}`
      const team = asStr(d.grantedToTeamName)
      return team
        ? `${from} shared ${what} with your team ${team}.`
        : `${from} shared ${what} with you.`
    },
    action: { route: 'view-folder', label: 'View folder' },
    buildAction: viewSharedAction,
  },
  // Restore-side mirror of folder_shared: a previously shared folder came back
  // (owner restored it from the bin). Distinct copy so a recovery doesn't read as
  // a brand-new share. Fans out to the recipient org's `manage_connection:write`
  // holders (no single addressee on a restore) — see restore-notify.ts.
  folder_share_restored: {
    icon: 'share',
    group: 'connection',
    buildTitle: (d) => `Shared ${sharedNoun(d)} restored`,
    buildBody: (d) => {
      const from = asStr(d.initiatorOrgName) ?? 'Someone'
      const sf = sharedFoldersPhrase(d)
      return sf
        ? `${from}'s ${sf.text} is available again.`
        : `A ${sharedNoun(d)} shared by ${from} is available again.`
    },
    action: { route: 'view-folder', label: 'View folder' },
    buildAction: viewSharedAction,
  },
  // #773 — a re-share that REUSES the recipient org's `.shared` places nobody (the
  // recipient org decides who manages its pointer), so the contact it names may not be
  // able to open it. This goes to THEM instead of the folder_shared / "added to your
  // Drive" ping, and deliberately carries NO action: there is nothing they can open.
  shared_reused_no_access: {
    icon: 'share',
    group: 'connection',
    buildTitle: (d) => `Shared ${sharedNoun(d)} already in your organization`,
    buildBody: (d) => {
      const from = asStr(d.initiatorOrgName) ?? 'Someone'
      const sf = sharedFoldersPhrase(d)
      return `${from} shared ${sf ? sf.text : `a ${sharedNoun(d)}`} with you. Your organization already has it, and you don't have access yet — someone who manages it can add you.`
    },
  },
  // …and to the people who MANAGE that `.shared` in the recipient org (who the access
  // dialog lists under Manage): someone was named on it and can't open it. Opens the
  // `.shared`; whether to add them is theirs to decide.
  shared_contact_needs_access: {
    icon: 'share',
    group: 'connection',
    buildTitle: (d) => `${asStr(d.contactName) ?? asStr(d.contactEmail) ?? 'Someone'} was named on a shared ${sharedNoun(d)}`,
    buildBody: (d) => {
      const from = asStr(d.initiatorOrgName) ?? 'Someone'
      const who = asStr(d.contactName) ?? asStr(d.contactEmail) ?? 'them'
      const sf = sharedFoldersPhrase(d)
      return `${from} shared ${sf ? sf.text : `a ${sharedNoun(d)}`} with ${who}. Your organization already had it, so they don't have access yet — add them if they should.`
    },
    action: { route: 'view-folder', label: 'View folder' },
    buildAction: viewSharedAction,
  },
  share_request_closed: {
    icon: 'alert',
    group: 'connection',
    buildTitle: () => 'Share request closed',
    // The org is gone, so there is nothing to open: no action (the bell renders it non-clickable).
    buildBody: (d) => {
      const org = asStr(d.recipientOrgName)
      const to = asStr(d.recipientEmail)
      const where = d.recipientKind === 'person'
        ? "The recipient's account was deleted"
        : org ? `${org} was deleted` : "The recipient's organization was deleted"
      // Neutral on purpose: the same text is right for whoever asked, a referral's source org, a personal drive, a file or a folder.
      return to ? `${where}, so the share request to ${to} was closed.` : `${where}, so the share request was closed.`
    },
  },
  chat_storage_more_space: {
    // The design draws an up-arrow; the bell's icon set has no such key and
    // widening it for one row is the wrong trade. `alert` is the closest
    // and is what every other row asking for a decision uses.
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => `${asStr(d.requesterName) ?? asStr(d.requesterEmail) ?? 'A member'} is asking for more chat space`,
    // The figures first, then their words. An admin acting on this needs
    // to know how short they are before they read the reason, and plenty
    // of requests carry no note at all.
    buildBody: (d) => {
      const used = asStr(d.usedLabel)
      const limit = asStr(d.limitLabel)
      const where = used && limit ? `${used} of ${limit}` : undefined
      const note = asStr(d.note)
      return [where, note ? `\u201C${note}\u201D` : undefined].filter(Boolean).join(' · ') || undefined
    },
    // TWO actions, because the admin's two replies are equally normal.
    // With one, saying no becomes "ignore the row" — and an ignored
    // request is exactly what the member cannot tell from a declined one.
    action: { route: 'org-storage', label: 'Adjust limit' },
    secondaryAction: { route: 'chat-storage-decline', label: 'Decline' },
  },
  chat_storage_raised: {
    icon: 'check',
    group: 'system',
    // SENT IN BOTH DIRECTIONS. The route's only guard is
    // `beforeLimit === afterLimit`, so a LOWERING arrives here too — and it
    // used to arrive as "Your chat storage limit is now 0 MB" under a green
    // check, with "0 MB free" as the body. A tick for the event that just
    // blocked them (full review 43C, A3 bonus).
    //
    // `icon` is static per type, so a tightening cannot take the alert
    // icon without a second notification type — which is a behaviour
    // change beyond this PR's scope. The TITLE carries the meaning
    // instead, and is explicit about the direction. That the icon still
    // reads "check" on a lowering is recorded in the PR body.
    buildTitle: (d) => {
      const limit = asStr(d.limitLabel)
      // A RAISE that still leaves them over (100 MB → 200 MB while holding
      // 300 MB) is a DIFFERENT state: it carries `noHeadroom`, not
      // `blocked`, and keeps its ordinary title, because titling real good
      // news "you can no longer attach" would be its own wrong statement.
      //
      // So `blocked` alone decides this, with no `&& loosened === false`.
      // A guard like that was written here and then deleted — but NOT for
      // the reason the deleting commit gave. It claimed the combination was
      // unreachable because `loosened` needs `afterLimit > beforeLimit` and
      // nothing is below 0. That argument is about `afterLimit`, and
      // `blocked` does not come from `afterLimit`: the route reads it from a
      // SECOND, later `memberChatStorageFor` taken after the stamp under no
      // lock, so another admin cutting the limit to 0 in that window really
      // does produce `blocked: true` with `loosened: true`. Dropping the
      // guard is still right — the settled state IS blocked, and that is
      // what the member needs to read — but the next person must not be
      // able to rebuild the guard from a false premise (scoped review of
      // b305e22bc, C1).
      if (d.blocked) return 'You can no longer attach files in chat'
      // A raise TO unlimited has no figure to name, and the generic
      // fallback ("is now higher") drops the one thing the member came
      // for. Every other surface says "No limit" in this state (final
      // full review, B3's sibling).
      if (d.unlimited) return 'Your chat storage is now unlimited'
      if (d.loosened === false) return `Your chat storage limit was lowered to ${limit ?? 'less'}`
      return `Your chat storage limit is now ${limit ?? 'higher'}`
    },
    // Who decided, and the one number the member came for. "Approved your
    // request" only when there WAS one — an admin raising a limit nobody
    // asked about is a different, pleasanter event.
    //
    // THREE STATES, not two, and the first version of this collapsed two of
    // them. "0 B free" reads as a tight fit rather than as a block, so it is
    // replaced — but by WHICH sentence depends on whether clearing space
    // would help:
    //
    //   limit 0            → nothing they delete raises a ceiling of 0; only
    //                        an admin can help.
    //   over a real limit  → they cannot attach either, but deleting DOES
    //                        work. Telling them it does not would be the
    //                        same class of lie in the other direction.
    //   otherwise          → the free figure, which means what it says.
    //
    // The first version keyed only on `blocked` (limit === 0), so a lowering
    // to a nonzero limit the member was already over rendered "0 B free" —
    // the exact shape this comment was written to forbid, two lines above
    // the code that still produced it (scoped review of full review 43,
    // finding 3).
    //
    // The NOTE is rendered here. It was collected (a labelled field on
    // Adjust limit), trimmed to 280 chars by the route "because it reaches
    // a bell", persisted — and read by nothing, so an admin who typed
    // "bumped you for the Q4 deck" sent it into the void while believing
    // they had written to the member (full review 43B, finding 1). The ask
    // direction already rendered its own note, which is where the symmetry
    // was assumed.
    buildBody: (d) => {
      const who = asStr(d.approverName) ?? asStr(d.approverEmail)
      const what = d.answeredRequest ? 'approved your request' : 'changed your limit'
      const free = asStr(d.freeLabel)
      const note = asStr(d.note)
      const where = d.blocked
        ? 'Ask an admin for space — deleting files will not help'
        : d.noHeadroom
          ? 'You have no space left — clear space to attach again'
          : (free ? `${free} free` : undefined)
      return [who ? `${who} ${what}` : undefined, where, note ? `\u201C${note}\u201D` : undefined]
        .filter(Boolean).join(' · ') || undefined
    },
    action: { route: 'org-storage', label: 'Manage chat storage' },
  },
  chat_storage_cleanup_scheduled: {
    // A warning about something that has not happened yet, so it is an
    // alert — but the member is NOT powerless here, which is what the body
    // and the action are for.
    icon: 'alert',
    group: 'system',
    // ⚠️ "NOW" IS NOT A DATE (D35). A cleanup an admin started now is a
    // booking too, so it sends this bell — and "On 22 Oct, 14:03" read as
    // notice the member did not get: the worker is already on it.
    buildTitle: (d) => d.startsNow === true
      ? 'Some of your idle conversations are being cleared now'
      : 'Some of your idle conversations will be cleared',
    // Says WHEN first. That date is the only part the member has to act
    // on, and a body that opens with who or why buries it.
    //
    // The star is named only when it still protects them. "Starred
    // conversations are kept" would be a false promise on a cleanup the
    // admin ticked the override for, and that is exactly the cleanup where
    // being misled costs most.
    buildBody: (d) => {
      const when = asStr(d.runAt)
      const days = typeof d.idleDays === 'number' ? d.idleDays : null
      const who = asStr(d.actorName) ?? asStr(d.actorEmail)
      const date = when ? new Date(when) : null
      const whenText = date && !Number.isNaN(date.getTime())
        ? date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : null
      if (d.startsNow === true) {
        return [
          days !== null
            ? `Conversations already idle for over ${days} days are being deleted now, with the files in them`
            : 'Idle conversations are being deleted now',
          // ⚠️ NO PROMISE ABOUT THE STAR. Stars are read when each pass of
          // up to 200 is planned, not before every deletion — so on a list
          // that size a star added now is read too late, and "star one to
          // keep it" would be a promise the run breaks.
          d.includeStarred
            ? '· starred ones are included'
            : '· it is already under way, so starring one now may be too late',
          who ? `· started by ${who}` : undefined,
        ].filter(Boolean).join(' ') || undefined
      }
      return [
        whenText ? `On ${whenText}` : 'Soon',
        // ⚠️ "ALREADY IDLE", AND THE STAR NAMED AS THE ONLY ESCAPE.
        //
        // This said "conversations with no activity for over N days will
        // be deleted", which was true until D31 and is now wrong in the
        // one direction that costs the member work: the list is FROZEN
        // when the admin books it, so replying to a conversation during
        // the notice week — the natural reading of "no activity" — does
        // not save it. Only a star does, and only while the admin has
        // not overridden stars. The member's own page carries a long
        // comment about exactly this hazard; the bell is where most
        // people will meet it.
        days !== null
          ? `conversations already idle for over ${days} days will be deleted, with the files in them`
          : 'idle conversations will be deleted',
        d.includeStarred
          ? '· starred ones are included, so starring will not keep one'
          : '· star one to keep it — replying to it will not',
        who ? `· booked by ${who}` : undefined,
      ].filter(Boolean).join(' ') || undefined
    },
    // Where they go to keep something: their own conversation list.
    action: { route: 'org-storage', label: 'Review my conversations' },
  },
  chat_storage_cleaned: {
    // ALERT, not check. Something of theirs was deleted by somebody else.
    // A tick would be the same mistake `chat_storage_raised` records in its
    // own comment: the icon is static per type, so it has to suit the worst
    // reading of the event, and that reading here is "my work is gone".
    icon: 'alert',
    group: 'system',
    // The count is the point, and the singular is worth its own branch: "1
    // conversations" in a message about deleted work reads as a system that
    // was not paying attention to what it deleted.
    // THREE states, because the two facts are independent. A member can
    // lose a conversation of their own, get space back from somebody
    // else's conversation they had uploaded into, or both — and the first
    // cut only had words for the first, so the second rendered "0 idle
    // conversations of yours were cleared".
    // ⚠️ `ended` IS THE ONE-BELL FORM (⑤, Season 2026-10-22). A member's
    // cleanup is told ONCE, when it ends, with what it took in all — so the
    // bell has to say HOW it ended as well as what went, including the
    // endings where nothing of theirs did: it was called off, stopped
    // before reaching them, found nothing, or could not be carried out.
    // Without a title for those, the last thing they heard was "will be
    // cleared". (The flags `more` / `gaveUp` / `stopped` below are the
    // per-pass form, still used for the unattributed target.)
    buildTitle: (d) => {
      const n = typeof d.count === 'number' ? d.count : 0
      if (n === 0 && !asStr(d.freedLabel) && !asStr(d.heldLabel)) {
        if (d.ended === 'cancelled') return 'A cleanup of your idle conversations was called off'
        if (d.ended === 'stopped' || d.stopped === true) return 'A cleanup of your idle conversations was stopped'
        if (d.ended === 'gaveUp') return 'A cleanup of your idle conversations could not be carried out'
        if (d.ended === 'done') return 'A cleanup of your idle conversations found nothing to clear'
      }
      if (n === 0) return 'An admin cleared idle conversations holding your files'
      return n === 1
        ? 'An idle conversation of yours was cleared'
        : `${n} idle conversations of yours were cleared`
    },
    // Who, how idle, and what it gave back — in that order, because "who"
    // is the first thing a member asks when their work disappears.
    // `freedLabel` is the bytes that left THIS member's own figure — the
    // route groups by `uploaded_by_email`, the same key their figure on the
    // Storage page is grouped by. It is ABSENT when none did, and the
    // sentence goes with it: the first cut sent the conversation's whole
    // size to its owner, so somebody whose colleague had uploaded the files
    // was told "this gave you back 300 MB" while their figure did not move.
    //
    // NO "this cannot be undone" sentence. It is true, it is also the one
    // thing the member can do nothing with, and saying it is the difference
    // between informing somebody and taunting them. The deletion is already
    // in the past tense in the title.
    buildBody: (d) => {
      const who = asStr(d.actorName) ?? asStr(d.actorEmail)
      const days = typeof d.idleDays === 'number' ? d.idleDays : null
      const freed = asStr(d.freedLabel)
      const n = typeof d.count === 'number' ? d.count : 0
      // WHO ENDED IT is not who booked it: an admin may stop another's
      // cleanup. `endedByName` is the stopper when the route knew; a
      // worker winding a stop down does not, and says "an admin".
      const endedBy = asStr(d.endedByName) ?? asStr(d.endedByEmail)
      // ⚠️ "RECORDED", NOT "NOTHING WAS". The count is a lower bound (D34):
      // a chunk a worker was killed in is gone and uncounted.
      if (n === 0 && !freed && !asStr(d.heldLabel)) {
        if (d.ended === 'cancelled') return [endedBy ? `${endedBy} called it off` : 'An admin called it off', 'before it ran · nothing of yours was cleared'].join(' ')
        if (d.ended === 'stopped' || d.stopped === true) {
          return [endedBy ? `${endedBy} stopped it` : 'It was stopped by an admin', '· no deletions of yours were recorded'].join(' ')
        }
        if (d.ended === 'gaveUp') return 'It stopped before any deletion of yours was recorded, and will not be retried'
        if (d.ended === 'done') return 'It ran, and no deletions of yours were recorded'
      }
      const held = asStr(d.heldLabel)
      return [
        who ? `${who} cleared conversations with no activity` : 'Conversations with no activity were cleared',
        days !== null ? `for over ${days} days` : undefined,
        // "AT LEAST", because the figure behind it is a lower bound at BOTH
        // emit sites that share this registry entry — for different reasons,
        // which is the part that kept getting written wrong:
        //
        //   `notifyCleanupEnded` — the member's ONE bell (⑤) — reads
        //     `released_bytes` off the booking row, which IS a lower bound:
        //     a pass whose worker was killed loses its chunk's share, and
        //     during a rolling deploy an older worker records none of it.
        //     Said flatly it can understate by a large factor with nothing
        //     marking it, which is the bug this wording closes.
        //   `notifyCleanupResult` — the per-pass bell the UNATTRIBUTED
        //     target sends — passes that pass's own figure, so it does not
        //     have the killed-worker or rolling-deploy gap. It is STILL a
        //     lower bound: the released figure is computed once per chunk
        //     inside a catch, so a chunk whose query failed drops out of
        //     the sum while the others count. (A round-5 note here claimed
        //     it was EXACT for that pass, and a round-6 review found both
        //     that the claim was false and that I had said in a commit
        //     message I had removed it. Two instances of the same mistake
        //     about one comment.)
        //
        // So one string serves both, and the hedge is right in both.
        //
        // The ceiling rides beside it below, where it adds something.
        freed ? `· this gave you back at least ${freed}` : undefined,
        // The one-bell form (⑤): what those conversations HELD — not "gave
        // you back", because it includes files with no recorded uploader,
        // which were never in her own figure.
        held ? `· they held ${held} of files` : undefined,
        // One cleanup can run in several passes, and each pass notifies.
        // Without this the member got three bells inside a minute, the
        // first two of which described a finished event that was not
        // finished.
        d.more === true ? '· more of them are still being cleared' : undefined,
        // ⚠️ THE ENDING THAT IS NOT AN ENDING. A cleanup that runs out
        // of attempts stops with work still on its list, and without
        // this its last notification is word-for-word the one a
        // finished cleanup sends. Nothing else tells the member: the
        // booking is marked `failed`, and `failed` has no screen.
        d.gaveUp === true ? '· some of them could not be cleared, and were left' : undefined,
        // An admin stopped it; this was its last batch (D35 step ④).
        d.stopped === true || d.ended === 'stopped'
          ? (endedBy ? `· then ${endedBy} stopped it, and nothing else will be cleared by it` : '· then it was stopped, and nothing else will be cleared by it')
          : undefined,
        // The one-bell form's own ending that is not an ending (⑤).
        // ⚠️ IT SAYS WHAT DID HAPPEN FIRST (Season 2026-10-28). The
        // sentence this replaces — "it stopped before it was finished, and
        // will not be retried" — is true, and read after a body that has
        // just counted her conversations and her megabytes it still lands
        // as "so none of that counted". It did count: every batch commits
        // as it goes. So the ending names the work before the limit.
        //
        // One sentence for BOTH live endings (ran out of retries, or its
        // worker went away), because the difference is only actionable for
        // the ADMIN — she cannot re-book her own cleanup, and the admin's
        // row keeps the two apart.
        //
        // Appended with the same ` · ` as everything else: the body is ONE
        // line, and making it two would mean changing how every
        // notification in the app renders (Season 2026-10-28: append).
        d.ended === 'gaveUp'
          ? '· the cleanup processed these conversations, but was interrupted before finishing the remaining items'
          : undefined,
      ].filter(Boolean).join(' ') || undefined
    },
    action: { route: 'org-storage', label: 'Manage chat storage' },
  },
  chat_storage_declined: {
    icon: 'alert',
    group: 'system',
    // States the outcome rather than the refusal: what the member needs to
    // know is where they now stand, not that somebody said no.
    // UNLIMITED HAS NO FIGURE, and the fallback made a sentence out of it
    // that is not one: the route deliberately omits `limitLabel` when the
    // ceiling is `null`, so an unlimited member read "Your limit stays at
    // the same." — with a body still telling them to delete conversations,
    // to someone who has no ceiling at all. `blocked` is false (null is not
    // 0), so the zero branch does not catch it either (final full review,
    // B3).
    //
    // Reachable through two already-shipped behaviours: a member is moved
    // to a role whose default is unlimited, which answers no open request
    // (D23), and a second admin later clears the stale row with Decline.
    buildTitle: (d) => (d.unlimited
      ? 'Your chat storage stays unlimited'
      : `Your limit stays at ${asStr(d.limitLabel) ?? 'the same'}`),
    // AT ZERO, BOTH HALVES OF THE OLD ADVICE WERE IMPOSSIBLE. A member
    // blocked at a 0 limit asks, is declined, and was told "Delete
    // finished conversations or move files to Drive to free space" —
    // nothing she deletes raises a ceiling of 0, and the Drive move is
    // refused by the same gate. Every other surface in this feature has a
    // zero branch; this was the one that did not (full review 43C, A3).
    //
    // The Drive half was wrong for a second reason, and the first version
    // of this comment over-generalised it: "a member at their limit cannot
    // move an attachment to Drive" is true only of an INLINE attachment
    // (under 10 MB and an inlinable mime), whose move re-uploads the bytes
    // through the member gate while the original is still live. A
    // handle-backed file goes straight to `save-to-drive`, which that gate
    // is not wired to, so the big ones always move. Either way this
    // sentence should not carry a rule about it — see
    // `StorageBlockedDialog` for the three versions of that rule that were
    // each wrong in a different direction.
    buildBody: (d) => d.blocked
      ? 'Ask an admin for space — deleting files will not help at a 0 limit'
      // Nothing to free and no reason to: they are not limited.
      : d.unlimited
        ? undefined
        : 'Delete conversations you no longer need to free space',
    action: { route: 'org-storage', label: 'Manage chat storage' },
  },
  connection_declined: {
    icon: 'alert',
    group: 'connection',
    // A recipient rejecting an incoming request (canReconnect — the initiator's
    // copy) is a "reject"; the initiator backing out post-pick is a "decline".
    buildTitle: (d) => d.shareDriven
      ? (d.canReconnect ? 'Your shared folder was declined' : 'Shared folder declined')
      : (d.canReconnect ? 'Connection request rejected' : 'Connection declined'),
    buildBody: (d) => {
      const pair = orgPairLine(d)
      const who = asStr(d.declinedByOrgName)
      const reason = asStr(d.note)
      const note = reason ? ` · Note: ${reason}` : ''
      // Share-driven: frame the folder, not the connection protocol.
      if (d.shareDriven) {
        const sf = sharedFoldersPhrase(d)
        const folder = sf ? sf.text : (d.canReconnect ? 'your shared folder' : 'the shared folder')
        return who ? `${who} declined ${folder}${note}.` : `The shared folder was declined${note}.`
      }
      // Recipient rejected the incoming request (pre-pick → no org pair).
      if (d.canReconnect) {
        return who ? `${who} rejected your connection request${note}.` : `Your connection request was rejected${note}.`
      }
      // Initiator declined after the recipient picked → "<who> declined
      // connection of OrgA ↔ OrgB · Note: <reason>."
      const head = who && pair ? `${who} declined connection of ${pair}`
        : who ? `${who} declined`
        : pair ? `Connection declined · ${pair}`
        : null
      if (head) return `${head}${note}.`
      return reason ? `Note: ${reason}.` : undefined
    },
    // Dead-end fix: an always-available next step instead of a shrug.
    action: { route: 'reconnect', label: 'Send a new request' },
  },
  reshare_send_failed: {
    icon: 'alert',
    group: 'reshare',
    buildTitle: (d) => `Couldn't reshare to ${d.recipientEmail ?? 'them'}`,
    buildBody: () => "You're now connected, but the reshare didn't go through (sharing may have changed). Try again.",
    action: { route: 'reshare-retry', label: 'Reshare again' },
  },
  // A spend/storage ceiling crossed. Shown in the bell (money surprises must not
  // be silent) and mirrored to email by sendBudgetThresholdEmail.
  //
  // ── Who gets an action, and why it is safe (A3) ─────────────────────
  //
  // This used to carry none at all, on the reasoning that "the budgets page is
  // admin-tier and the member scope has nothing to act on". Half of that is
  // still exactly right and is why `buildAction` is scope-aware rather than a
  // static field: the `user` scope is delivered to the MEMBER whose own
  // allocation ran low, and raising it is their admin's job, not theirs. A
  // button there would be a route out that ends in a refusal.
  //
  // The other half stopped being true when Budget became a real Settings
  // route. The tenant and storage scopes are delivered to the holders of
  // `manage_budget:write` (#1102 item 10, `notifyTenantCapabilityHolders`) —
  // exactly who the Budget page's editor admits, chosen at emit precisely
  // because they are the people who can act (defaults: owners and admins). So
  // the recipient set is already the permitted set, and the action needs no
  // second gate at render; the bell has no per-tenant capability data to gate
  // with anyway, since a user's notifications span every tenant they belong to.
  //
  // If the emit-side set is ever widened BEYOND the page's own gate (another
  // capability, a read level), this action must gain a real gate in the same
  // change — it is load-bearing, not incidental.
  budget_threshold: {
    icon: 'alert',
    group: 'system',
    // Allow-list, not `!== 'user'`. The title copy below treats an ABSENT
    // scope as org-scoped, which is a safe default for a sentence and not for
    // an action: a legacy row missing the field could have been the member's
    // own, and offering that reader a button is the one failure this split
    // exists to prevent. An unknown scope gets copy but no action.
    buildAction: (d) =>
      d.budgetScope === 'tenant_free' || d.budgetScope === 'tenant_extra' || d.budgetScope === 'storage'
        ? { route: 'org-budget', label: 'View budget' }
        : undefined,
    buildTitle: (d) => {
      const org = d.budgetScope !== 'user'
      const month = monthName(d.period)

      if (d.budgetScope === 'storage') {
        if (d.thresholdKind === 'abs') {
          const used = typeof d.usedBytes === 'number' ? formatBytes(d.usedBytes) : undefined
          return used ? `Your organization's storage reached ${used}` : 'Storage threshold reached'
        }
        const pct = typeof d.thresholdValue === 'number' ? d.thresholdValue : undefined
        return pct === undefined
          ? 'Storage threshold reached'
          : `Your organization has used ${pct}% of its storage`
      }

      // `abs` fires on unlimited axes too, so it states the amount reached and
      // never a proportion — there may be no denominator to be a fraction of.
      if (d.thresholdKind === 'abs') {
        const amount = typeof d.usedBillingMicro === 'number' ? formatMoneyLabeled(d.usedBillingMicro, budgetMoney(d)) : undefined
        if (!amount) return 'AI spend threshold reached'
        const bucket = d.budgetScope === 'tenant_free' ? 'included ' : d.budgetScope === 'tenant_extra' ? 'extra ' : ''
        const head = org ? `Your organization's ${bucket}AI spend` : 'Your AI spend'
        return `${head} reached ${amount}${month ? ` in ${month}` : ''}`
      }

      const pct = typeof d.thresholdValue === 'number' ? d.thresholdValue : undefined
      if (pct === undefined) return 'AI budget threshold reached'
      const budget = d.budgetScope === 'tenant_free' ? 'its included AI budget'
        : d.budgetScope === 'tenant_extra' ? 'its extra AI budget'
        : 'your AI budget'
      const head = org ? 'Your organization has' : "You've"
      return `${head} used ${pct}% of ${budget}${month ? ` for ${month}` : ''}`
    },
    buildBody: (d) => {
      if (d.budgetScope === 'storage') {
        if (typeof d.usedBytes !== 'number') return undefined
        return budgetUsageLine(d.usedBytes, d.capBytes, formatBytes, 'storage')
      }
      if (typeof d.usedBillingMicro !== 'number') return undefined
      const money = budgetMoney(d)
      const month = monthName(d.period)
      // Mark the LINE once, not each figure — "≈$40 of ≈$50" reads as two
      // separate approximations rather than one converted statement.
      const line = budgetUsageLine(d.usedBillingMicro, d.capBillingMicro, (n) => formatMoney(n, money), 'budget', month ? ` in ${month}` : '')
      if (!line) return undefined
      return isApproximateCurrency(money.currency, money.rate)
        ? `${APPROXIMATE_CURRENCY_MARKER}${line}`
        : line
    },
  },
  // The prepaid balance dropped to the warning point (usage credits, C4).
  //
  // With overage dropped and no auto-reload, this is the ONLY warning between
  // a working org and a hard stop — hence the bell rather than a log line.
  // Owners/admins only: they are the people who can act on it.
  usage_credits_low: {
    icon: 'alert',
    group: 'system',
    /**
     * The body says "Top up on the Billing page"; this is the page.
     *
     * It had none while `budget_threshold` — same family, same emit-side
     * restriction to owners and admins — has had one since A3. So the alert
     * whose entire purpose is "act before AI stops" named the action and did
     * not offer it, which is the defect A1 fixed for the 402 and A3 fixed for
     * the budget alert.
     *
     * UNCONDITIONAL, unlike `budget_threshold`'s allow-list on `budgetScope`.
     * That one has to discriminate because a `user`-scoped row is addressed to
     * a member who cannot act, and a legacy row with no scope might be one.
     * This type has no such split: the `manage_billing:write` holders' fan-out is
     * its only emitter, so every row that exists is already addressed to someone who
     * may buy credit. The recipient set IS the permitted set — the same
     * argument A3 landed on, and the reason no permission field is needed here.
     */
    buildAction: () => ({ route: 'org-billing', label: 'Top up' }),
    buildTitle: (d) => {
      const bal = typeof d.creditBalanceBillingMicro === 'number'
        ? formatMoneyLabeled(d.creditBalanceBillingMicro, budgetMoney(d))
        : undefined
      if (bal === undefined) return 'AI usage credits running low'
      // Zero is a different statement from "running low" — the org has already
      // stopped, and telling them to plan ahead is a beat too late.
      return d.creditBalanceBillingMicro === 0
        ? 'AI usage credits are used up'
        : `AI usage credits running low — ${bal} left`
    },
    buildBody: (d) => {
      const money = budgetMoney(d)
      const threshold = typeof d.creditThresholdBillingMicro === 'number'
        ? formatMoney(d.creditThresholdBillingMicro, money)
        : undefined
      const tail = 'Top up on the Billing page to keep AI running once the included budget is gone.'
      // Only claim a limit was crossed when the admin actually SET one; the
      // derived default is our heuristic, not their decision.
      if (!threshold || d.creditThresholdSource !== 'explicit') return tail
      // Mark the converted figure, not the whole paragraph — the tail carries
      // no money and an `≈` in front of it reads as hedging the advice.
      const shown = isApproximateCurrency(money.currency, money.rate)
        ? `${APPROXIMATE_CURRENCY_MARKER}${threshold}`
        : threshold
      return `This is at or below your ${shown} low-balance alert. ${tail}`
    },
  },
  // A promo grant landed on the tenant — admin-issued or code-redeemed (promo
  // plan P5). Bell + toast, no email: for a redemption the actor is holding the
  // app, and for an admin grant there is no deadline to chase someone about.
  // The expiry notice below is the one that has to reach a closed laptop.
  promo_granted: {
    icon: 'check',
    group: 'system',
    // A code redemption is somebody on this tenant acting; an admin grant is
    // something done TO it. Both fan out to every owner/admin, so neither may
    // say "you" — the other admins did not do it.
    buildTitle: (d) => d.promoSource === 'code' ? 'Promo code redeemed' : 'Promotional access added',
    buildBody: (d) => {
      const money = budgetMoney(d)
      const { text, hasMoney } = promoEffectsPhrase(d, money)
      const what = text ?? 'Promotional access'
      const until = promoDate(d.promoEndsAt)
      const line = until ? `${what} — active until ${until}.` : `${what} is now active.`
      // Capitalize when the phrase leads the sentence; effect phrases are
      // written lower-case so they also read correctly mid-sentence.
      const sentence = line.charAt(0).toUpperCase() + line.slice(1)
      return markApproximate(sentence, money, hasMoney)
    },
  },
  // The grant's window closes soon (fired at T-3 days by the promo sweep,
  // claim-before-emit via `promo_grants.expiry_notified_at`).
  //
  // `promoDaysLeft` is stamped at emit and never recomputed — the bell
  // re-describes from `data` on every render, so a live countdown would tick
  // past zero into nonsense. The absolute date in the body is what keeps a
  // stale row honest.
  promo_expiring: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => {
      const days = typeof d.promoDaysLeft === 'number' ? d.promoDaysLeft : undefined
      const when = days === undefined ? 'soon'
        : days <= 0 ? 'today'
        : days === 1 ? 'tomorrow'
        : `in ${days} days`
      const remaining = d.promoCreditRemainingBillingMicro
      // Lead with the money when there is unspent credit — that is the part
      // the tenant loses. A grant with none left is about access ending.
      if (typeof remaining === 'number' && remaining > 0) {
        return `${formatMoneyLabeled(remaining, budgetMoney(d))} of promo credit expires ${when}`
      }
      return `Promotional access ends ${when}`
    },
    buildBody: (d) => {
      const money = budgetMoney(d)
      const { text, hasMoney } = promoEffectsPhrase(d, money)
      const what = text ?? 'Promotional access'
      const until = promoDate(d.promoEndsAt)
      const head = until ? `${what} ends on ${until}.` : `${what} is ending.`
      const sentence = head.charAt(0).toUpperCase() + head.slice(1)
      const remaining = d.promoCreditRemainingBillingMicro
      // Only warn about forfeiture when there is something to forfeit — on a
      // fully-drawn or credit-less grant it would be advice about nothing.
      const tail = typeof remaining === 'number' && remaining > 0
        ? ' Unused promotional credit does not carry over.'
        : ''
      return markApproximate(sentence, money, hasMoney) + tail
    },
  },
  // Review fixes §3.4: a link made from a connection is a signal after the fact (Claude asked before running the tool);
  // the review dialog (keep or revoke) is the way back.
  // #1229 — privileged changes. A change to who holds platform power, which AI apps everyone sees as verified, or
  // whether an org lets AI apps act in it, is told to the owners/admins who own that power, beside the stored record
  // (privileged_changes). After the fact, like Zeabur's "alerts for privileged changes": nobody should learn of one
  // from a log line.
  platform_admin_changed: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => (d.changeAfter === 'admin'
      ? `${asStr(d.changeSubject) ?? 'Someone'} is now a platform admin`
      : `${asStr(d.changeSubject) ?? 'Someone'} is no longer a platform admin`),
    buildBody: (d) => `Changed by ${asStr(d.changeActor) ?? 'the system'}. Platform admins can see and change every organization on this deployment.`,
  },
  mcp_verified_app_changed: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => {
      const host = asStr(d.changeSubject) ?? 'an app'
      if (d.changeAfter === 'removed') return `${host} is no longer a verified AI app`
      if (d.changeAfter === 'logo') return `${host}'s logo was changed`
      return `${host} is now a verified AI app`
    },
    buildBody: (d) => `Changed by ${asStr(d.changeActor) ?? 'the system'}. Everyone connecting an app from a verified host sees it as trusted, with its logo.`,
  },
  mcp_connections_switch_changed: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => (d.changeAfter === 'on'
      ? 'AI app connections were turned on for this organization'
      : 'AI app connections were turned off for this organization'),
    buildBody: (d) => `Changed by ${asStr(d.changeActor) ?? 'the system'}.`,
  },
  // #1297: the org policy above each connection's keybook switch.
  mcp_secrets_switch_changed: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => (d.changeAfter === 'on'
      ? 'Connected AI apps may now use this organization\u2019s keybook secrets'
      : 'Connected AI apps may no longer use this organization\u2019s keybook secrets'),
    buildBody: (d) => (d.changeAfter === 'on'
      ? `Changed by ${asStr(d.changeActor) ?? 'the system'}. Each member still decides, per connection, on Connected apps.`
      : `Changed by ${asStr(d.changeActor) ?? 'the system'}. App runs from connected AI apps get no secrets, whatever their switch says.`),
  },
  share_link_from_connection: {
    icon: 'link',
    group: 'system',
    buildTitle: (d) => {
      const what = asStr(d.linkTargetName) ?? 'an item'
      const who = asStr(d.connectionName) ?? 'a connected app'
      return d.linkAudience === 'public'
        ? `A public link to ${what} was created from ${who}`
        : `A link to ${what} was created from ${who}`
    },
    buildBody: (d) => {
      const people = Array.isArray(d.linkRecipients) ? d.linkRecipients.filter((x): x is string => typeof x === 'string') : []
      const audience = d.linkAudience === 'public'
        ? 'Anyone with the link can open it.'
        : people.length ? `Only ${people.slice(0, 3).join(', ')}${people.length > 3 ? ` and ${people.length - 3} more` : ''} can open it.` : 'Only the people listed can open it.'
      // Just who can open it — the "Review link" action says what to do (a second sentence made every row wordy).
      return audience
    },
    // Opens a review dialog (who, when, through which connection, who can open it, last opened) with Keep / Revoke —
    // never a revoke on click: the person decides with the facts in front of them.
    action: { route: 'review-share-link', label: 'Review link' },
  },
  // #1320 step 1 — a move whose unit of work rolled back AND whose disk undo then failed: the drive kept the item where it
  // was, but on disk it sat where it was being moved. Recorded for reconcile (a repair puts the files back at their
  // drive place); the mover is told so a missing or stale file is no mystery. No action (nothing for them to do).
  move_disk_undo_failed: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => `A move was undone${asStr(d.itemName) ? `: ${asStr(d.itemName)}` : ''}`,
    buildBody: () => "The move didn't go through, so the item stays where it was. Its files are being put back in step in the background — if one looks out of date for a little while, that's why.",
  },
  // #982: a move across organisations landed its copy, the original could not be removed, and the copy could not be
  // taken back after every retry. Nothing is lost — both are there — but the person asked for a MOVE and must be told
  // they now have two. Delivered to the mover only (the request's identity), as a log line also records it.
  move_incomplete: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => `A move didn't finish${asStr(d.itemName) ? `: ${asStr(d.itemName)}` : ''}`,
    buildBody: () => "The original couldn't be removed, and the copy that already arrived couldn't be taken back. Both are there now — delete the one you don't want.",
  },
  // #1101 — a background db operation the person's request started ended out of band (the request had already
  // answered). Stored ALWAYS, beside the DB editor's live notice: it fails rarely, and by then the person has often
  // left the editor or closed the tab. No action (a db reached through a `.shared` has no drive-navigable id here).
  // A new operation / outcome adds its copy HERE and in the editor's `database.operationEnded.*` keys.
  db_operation_ended: {
    icon: 'alert',
    group: 'system',
    buildTitle: (d) => (d.dbOperation === 'version-save' && d.dbOutcome === 'not-saved'
      ? 'A database version wasn’t saved'
      : d.dbOperation === 'replace' && d.dbOutcome === 'replaced' ? 'A database was replaced'
        : d.dbOperation === 'replace' && d.dbOutcome === 'not-applied' ? 'A database wasn’t replaced'
          : d.dbOperation === 'replace' && d.dbOutcome === 'failed' ? 'A database replace needs attention'
            : 'A database operation ended'),
    buildBody: (d) => {
      const name = asStr(d.dbFileName)
      const db = name ? `“${name}”` : 'the database'
      const Db = name ? `“${name}”` : 'The database'
      // #1090 — a large Replace run in the background.
      if (d.dbOperation === 'replace') {
        if (d.dbOutcome === 'replaced' && d.dbReason === 'damaged') return `${Db} was replaced. Its previous contents could not be read, so they are not in its version history.`
        if (d.dbOutcome === 'replaced') return `${Db} was replaced. Its previous contents are kept in its version history.`
        if (d.dbOutcome === 'failed' && d.dbReason === 'resolved') return `An administrator ended the paused replace of ${db} and left it as it is now. Check that its contents are what you expect.`
        if (d.dbOutcome === 'not-applied' && d.dbReason === 'resolved') return `An administrator put ${db} back as it was before the replace. The new contents were not applied.`
        if (d.dbOutcome === 'failed') return `Replacing ${db} could not be finished, and it is paused until an administrator resolves it. Nothing was deleted.`
        if (d.dbOutcome !== 'not-applied') return undefined
        if (d.dbReason === 'invalid') return `The new contents are not a sound SQLite database, so ${db} was left unchanged.`
        if (d.dbReason === 'busy') return `${Db} stayed busy, so it was not replaced and was left unchanged. Try again later.`
        if (d.dbReason === 'gone') return `${Db} was deleted or moved before it could be replaced.`
        if (d.dbReason === 'error') return `Something went wrong while replacing ${db}, so it was left unchanged. Try again.`
        return `Replacing ${db} was refused (your access may have changed, or the drive is full), so it was left unchanged.`
      }
      if (d.dbOperation !== 'version-save' || d.dbOutcome !== 'not-saved') return undefined
      if (d.dbReason === 'too-large') {
        return `${name ? `${db} is` : 'The database is'} too large to copy in the time a version save may take, so no version of your change was saved. The change itself is in the database.`
      }
      // E7 PR 2 slice 2: a newer drive version replaces the change — never "the change itself is in the database".
      if (d.dbReason === 'superseded') {
        return `A newer version of ${db} was saved in the drive while your change was being recorded, so your change was not saved and the drive’s version will replace it.`
      }
      return `Saving a version of your change to ${db} was refused (the drive may be full, or your access changed), so no version was saved. The change itself is in the database.`
    },
  },
  // Refused-write undo §5 — the settle pass finished a run that could not finish its own undo (it crashed, or its
  // processes could not be confirmed stopped). Counts only: never a path (the person may not see every folder the run
  // wrote in). English only, like every notification.
  refused_writes_undone: {
    icon: 'alert',
    group: 'system',
    buildTitle: () => 'Changes that weren’t saved were undone',
    buildBody: (d) => {
      const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0)
      const parts: string[] = []
      if (n(d.undoneCount)) parts.push(`${n(d.undoneCount)} change${n(d.undoneCount) === 1 ? '' : 's'} a run made ${n(d.undoneCount) === 1 ? 'was' : 'were'} not saved to the drive and ${n(d.undoneCount) === 1 ? 'was' : 'were'} undone`)
      if (n(d.leftCount)) parts.push(`${n(d.leftCount)} ${n(d.leftCount) === 1 ? 'was' : 'were'} left as ${n(d.leftCount) === 1 ? 'it is' : 'they are'} (a newer version is there, or ${n(d.leftCount) === 1 ? 'it' : 'they'} could not be undone)`)
      if (n(d.waitingCount)) parts.push(`${n(d.waitingCount)} will be undone once ${n(d.waitingCount) === 1 ? 'its' : 'their'} folder is free`)
      if (n(d.largeUncheckedCount)) parts.push(`${n(d.largeUncheckedCount)} large file${n(d.largeUncheckedCount) === 1 ? '' : 's'} couldn't be checked and ${n(d.largeUncheckedCount) === 1 ? 'was' : 'were'} left as ${n(d.largeUncheckedCount) === 1 ? 'it is' : 'they are'}`)
      if (n(d.unreadableCount)) parts.push(`${n(d.unreadableCount)} item${n(d.unreadableCount) === 1 ? '' : 's'} couldn't be read, so ${n(d.unreadableCount) === 1 ? 'it' : 'they'} couldn't be checked and ${n(d.unreadableCount) === 1 ? 'was' : 'were'} left as ${n(d.unreadableCount) === 1 ? 'it is' : 'they are'}`)
      return parts.length ? `${parts.join('; ')}.` : 'A run ended before it could finish; nothing it left needed undoing.'
    },
  },
  invite_refresh: { icon: 'bell', group: 'system', silent: true, buildTitle: () => '' },
  share_repointed: { icon: 'bell', group: 'system', silent: true, buildTitle: (d) => asStr(d.title) ?? 'Share updated' },
  share_repoint_failed: { icon: 'alert', group: 'system', silent: true, buildTitle: (d) => asStr(d.title) ?? 'Share update failed' },
}

export interface DescribedNotification {
  icon: NotificationIcon
  group: NotificationDescriptor['group']
  silent: boolean
  title: string
  body?: string
  action?: NotificationAction
  /** A second, lower-emphasis action beside the first.
   *
   *  Added for the chat-storage request (#758), where the two replies an
   *  admin has are equally normal — raising the limit and saying no. A
   *  single action would make one of them the affordance and the other a
   *  thing you do by ignoring the row, and an ignored request is exactly
   *  what the member cannot tell apart from a declined one. */
  secondaryAction?: NotificationAction
}

/** Resolve a notification (type + data) to its rendered shape. Falls back
 *  gracefully for unknown/legacy types — NEVER throws — so a single bad/old
 *  row can't crash the bell (the DB `type` column is free text). */
export function describeNotification(type: string, data: NotificationData = {}): DescribedNotification {
  const d = NOTIFICATION_REGISTRY[type as NotificationType]
  if (!d) {
    return { icon: 'bell', group: 'system', silent: false, title: asStr(data.title) ?? 'Notification', body: asStr(data.body) }
  }
  return {
    icon: d.icon,
    group: d.group,
    silent: !!d.silent,
    title: d.buildTitle(data),
    body: d.buildBody?.(data),
    action: d.buildAction?.(data) ?? d.action,
    secondaryAction: d.secondaryAction,
  }
}

/** Resolve a SETTLED request-bound notification to its outcome badge (+ optional
 *  next step), from the request's current state. Same graceful contract as
 *  `describeNotification`: unknown type / unknown-or-absent state → undefined,
 *  never throws, so the row simply renders as it does today.
 *
 *  Callers must only consult this once the row is no longer actionable — while
 *  the request still sits in `action.requestState` the ASK is the truth, not an
 *  outcome. */
export function resolveNotificationOutcome(
  type: string,
  state: string | undefined,
  data: NotificationData = {},
): ResolvedOutcome | undefined {
  if (!state) return undefined
  return NOTIFICATION_REGISTRY[type as NotificationType]?.resolveOutcome?.(state, data)
}

/** The notification-type → request-state map the server uses to stamp
 *  `actionable`, derived from the registry so it can't drift from the
 *  descriptors. Typed loosely (Record<string,string>) so the list endpoint can
 *  index it with the free-text DB `type` column directly. */
export const ACTIONABLE_STATE_BY_TYPE: Record<string, string> =
  Object.fromEntries(
    Object.entries(NOTIFICATION_REGISTRY)
      .filter(([, d]) => d.action?.requestState)
      .map(([t, d]) => [t, d.action!.requestState as string]),
  )
